import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { config } from "@config";
import { take } from "rxjs/operators";
import { BpStoriesTrackingFacade } from "@modules/shared/bp-stories/bp-stories-tracking.facade";
import { ActivatedRoute, Router } from "@angular/router";
import { BpStoriesFacade } from "@modules/shared/bp-stories/bp-stories.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
	selector: "bp-stories",
	templateUrl: "./bp-stories.component.html",
	styleUrls: ["./bp-stories.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [BpStoriesTrackingFacade],
	standalone: false
})
export class BpStoriesComponent implements OnInit, OnDestroy {
	@ViewChild("storylyWeb", { static: true }) storylyWebDom!: ElementRef;

	public isInit: boolean = false;

	constructor(
		private readonly storiesTrackingFacade: BpStoriesTrackingFacade,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly bpStoriesFacade: BpStoriesFacade
	) {}

	private initializeStoryly(userPropertyData: object): void {
		const storylyOptions = {
			token: config.storylyToken,
			customParameter: userPropertyData["{user_pid}"],
			userPropertyData,
			props: {
				storyGroupAlign: "left",
				scrollIndicator: true,
				scrollIndicatorOptions: {
					indicatorWidth: 40,
					indicatorHeight: 40,
					indicatorBorderRadius: 50,
					indicatorBorderColor: "#f2f4f7",
					indicatorDefaultIconsColor: "#282828"
				}
			},
			customStoryGroupHTML: `
					<div class="custom-story-group-container">
						<div class="custom-story-group">
							<div class="storyly__story-preview"></div>
							  <img class="storyly__story-group-cover" />
							  <span class="storyly__story-group-pin">
									<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="12" cy="12" r="11.5" fill="url(#paint0_linear_1714_2522)" stroke="white"/>
									<path d="M11.2835 7.54605C11.5769 6.95145 12.4248 6.95145 12.7182 7.54605L13.83 9.79866L16.3159 10.1599C16.9721 10.2552 17.2341 11.0616 16.7592 11.5244L14.9604 13.2778L15.3851 15.7537C15.4972 16.4072 14.8112 16.9056 14.2243 16.597L12.0009 15.4281L9.7774 16.597C9.19049 16.9056 8.50455 16.4072 8.61664 15.7537L9.04128 13.2778L7.24246 11.5244C6.76765 11.0616 7.02966 10.2552 7.68584 10.1599L10.1717 9.79865L11.2835 7.54605Z" fill="white"/>
									<defs>
									<linearGradient id="paint0_linear_1714_2522" x1="-10" y1="-2.5" x2="15" y2="27" gradientUnits="userSpaceOnUse">
									<stop stop-color="#E7B06F"/>
									<stop offset="1" stop-color="#FFDA7B"/>
									</linearGradient>
									</defs>
									</svg>
							   </span>
							   <div class="storyly__story-group-title"></div>
						</div>
					</div>
				`
		};
		const storyly = this.storylyWebDom.nativeElement;
		if (storyly) {
			storyly.init(storylyOptions);
			storyly.on("openStoryGroup", (group) => {
				const storylyParam = this.route.snapshot.queryParams["storyly"];
				const source = storylyParam ? "deeplink" : "storyBar";
				this.storiesTrackingFacade.trackStoryGroupClick(group.group_id, source);
				// this will remove the storyly query param from the url
				if (source === "deeplink") {
					this.router.navigate([], {
						queryParams: {
							storyly: null
						},
						queryParamsHandling: "merge",
						replaceUrl: true
					});
				}
			});
			storyly.on("userInteracted", (data) => {
				const type = data.story.componentList.find((c) => c.type).type;
				this.storiesTrackingFacade.trackUserInteraction(type, data.storyGroup.group_id, data.story.story_id);
			});
			storyly.on("closeStoryGroup", (group) => {
				this.storiesTrackingFacade.trackStoryGroupClosed(group.group_id);
			});
			storyly.on("storyViewed", (story) => {
				this.storiesTrackingFacade.trackStoryViewed(story.group.group_id, story.story_id);
			});
			storyly.on("actionClicked", (story) => {
				this.storiesTrackingFacade.trackStoryCTAClicked(story.group_id, story.id);
				if (story.actionUrl.includes("http")) {
					window.open(story.actionUrl, "_blank");
					return;
				}
				window.open(story.actionUrl, "_self");
			});
			storyly.on("loaded", (data) => {
				if (!this.isInit && data.allGroups.length >= 2) {
					this.storylyWebDom.nativeElement.removeAttribute("hidden");
					this.updateSegments();
				}
				this.isInit = true;
			});
		}
	}

	public ngOnInit(): void {
		this.bpStoriesFacade
			.getUserPropertyData()
			.pipe(take(1))
			.subscribe((userPropertyData) => {
				this.initializeStoryly(userPropertyData);
			});
	}

	public ngOnDestroy(): void {
		const tags = document.getElementsByTagName("storyly-stories");
		for (let i = 0; i < tags.length; i++) {
			tags[i].remove();
		}
	}

	private updateSegments(): void {
		this.bpStoriesFacade
			.getSegments$()
			.pipe(untilDestroyed(this))
			.subscribe((segments) => {
				this.storylyWebDom.nativeElement.setSegments(segments);
			});
	}
}
